import { cva, type VariantProps } from 'class-variance-authority'
import {
	Autocomplete as AriaAutocomplete,
	Button as AriaButton,
	ListBox as AriaListBox,
	ListBoxItem as AriaListBoxItem,
	Popover as AriaPopover,
	Select as AriaSelect,
	type SelectProps as AriaSelectProps,
	SelectValue as AriaSelectValue,
	type Key,
	useFilter,
} from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Icon } from '../../atoms/icon/icon'
import { Search } from '../search/search'

/**
 * The Dropdown set (201:608): Size = S (201:574), the white pill filter above a
 * table ("Brand: All"), and Size = L (204:3308), the 40px form select with a 1px
 * Stroke. Both draw the 20px chevron.
 *
 * S has two boolean chevrons, `iconLeft` and `iconRight`, both on by default as
 * in the component (201:574); the Work Orders list (321:25505) turns one off per
 * filter: the right one on filters, the left one on "Sort: Status". L's single trailing chevron is bound to the Figma property `iconLeft`
 * although it sits on the right; here it is `iconRight` on both sizes.
 *
 * L's 1px Stroke counts in its layout: the value and the chevron sit 17px from
 * each edge (16px padding inside the stroke), so it is a border, not an inset ring.
 *
 * Not drawn in the Figma, and so kept to the kit's own language:
 *
 * - the open menu: a popover in the card language, Light fill, 12px radius, Drop Shadow/2,
 *   whose options scroll past 320px, and with `searchable` a search field above them;
 * - L's placeholder, shown in Light dark while nothing is chosen;
 * - the keyboard focus ring, the kit's accent outline.
 */
export const dropdownTriggerVariants = cva(
	['inline-flex cursor-pointer items-center bg-atlas-light font-atlas-sans data-disabled:cursor-default', focusRing],
	{
		variants: {
			size: {
				s: 'h-8 max-w-full justify-center gap-0.5 rounded-atlas-full px-3 py-1.5',
				l: 'h-10 w-full gap-2.5 rounded-atlas-md border border-atlas-stroke px-4',
			},
		},
		defaultVariants: { size: 's' },
	},
)

const valueClass = {
	s: 'min-w-0 truncate text-atlas-label-medium text-atlas-pure-black',
	l: 'min-w-0 flex-1 truncate text-left text-atlas-body text-atlas-dark data-placeholder:text-atlas-light-dark',
}

/** One option of a Dropdown. */
export interface DropdownItem {
	id: Key
	label: string
}

export interface DropdownProps<T extends DropdownItem, M extends 'single' | 'multiple' = 'single'>
	extends Omit<AriaSelectProps<T, M>, 'className' | 'children' | 'items'>,
		VariantProps<typeof dropdownTriggerVariants> {
	className?: string
	/** The options, e.g. the brands a list can be filtered by. */
	items: T[]
	/**
	 * Size S only: the visible caption before the value, as drawn, e.g. "Brand:".
	 * It is hidden from assistive technology, so also pass `aria-label`.
	 */
	label?: string
	/** Size S only: the chevron before the caption; on by default, as in the component. */
	iconLeft?: boolean
	/** The chevron after the value. */
	iconRight?: boolean
	/** A search field atop the open menu that narrows the options by label, for long lists. */
	searchable?: boolean
}

/**
 * A dropdown built on react-aria's Select: the trigger opens a listbox on click,
 * Enter, Space or the arrow keys, and the choice is reported through `onChange`.
 * With `selectionMode="multiple"` the menu stays open, toggles each option and ticks
 * the chosen ones, and the trigger lists their labels (a multi-value filter is not drawn in the Figma).
 * Width is the caller's for L (the board draws 554px); S sizes to its content.
 */
export function Dropdown<T extends DropdownItem, M extends 'single' | 'multiple' = 'single'>({
	size,
	items,
	label,
	iconLeft = true,
	iconRight = true,
	searchable = false,
	className,
	...props
}: DropdownProps<T, M>) {
	const s = size ?? 's'
	const { contains } = useFilter({ sensitivity: 'base' })
	const listBox = (
		<AriaListBox
			items={items}
			renderEmptyState={() => <p className="px-3 py-2 text-atlas-label-medium text-atlas-light-dark">No results</p>}
			className="flex max-h-80 min-h-0 flex-1 flex-col overflow-y-auto outline-none"
		>
			{(item) => (
				<AriaListBoxItem
					id={item.id}
					textValue={item.label}
					className="flex cursor-pointer items-center justify-between gap-2 rounded-atlas-sm px-3 py-2 text-atlas-label-medium text-atlas-dark outline-none data-focused:bg-atlas-background data-selected:text-atlas-accent"
				>
					{({ isSelected }) => (
						<>
							{item.label}
							{isSelected && props.selectionMode === 'multiple' ? <Icon name="tick-02" /> : null}
						</>
					)}
				</AriaListBoxItem>
			)}
		</AriaListBox>
	)
	return (
		<AriaSelect {...props} className={cn('inline-flex min-w-0 font-atlas-sans', s === 'l' && 'flex w-full', className)}>
			<AriaButton className={dropdownTriggerVariants({ size: s })}>
				{s === 's' && iconLeft ? <Icon name="chevron-down-sm" /> : null}
				{s === 's' && label ? (
					<span aria-hidden="true" className="shrink-0 truncate text-atlas-label-medium text-atlas-light-dark">
						{label}
					</span>
				) : null}
				<AriaSelectValue className={valueClass[s]} />
				{iconRight ? <Icon name="chevron-down-sm" /> : null}
			</AriaButton>
			<AriaPopover
				offset={4}
				className="flex min-w-(--trigger-width) flex-col rounded-atlas-md bg-atlas-light p-1 font-atlas-sans shadow-atlas-drop-2 outline-none"
			>
				{searchable ? (
					<AriaAutocomplete filter={contains}>
						<Search aria-label="Search options" autoFocus className="mb-1 h-9 shrink-0" />
						{listBox}
					</AriaAutocomplete>
				) : (
					listBox
				)}
			</AriaPopover>
		</AriaSelect>
	)
}
